import { httpClient } from '@/shared/api/httpClient'

// ── Types ───────────────────────────────────────

export interface ChallengeResponse {
    challenge_id: string
    ttl: number
    /**
     * Имя бота (`wp_sites.telegram_bot_name`) — оттуда же, откуда его берут
     * рассылки. Приезжает вместе с челленджем, чтобы страница входа не
     * зависела от сборочной переменной фронта: та однажды не попала в сборку,
     * и кнопка «Войти через Telegram» молча не работала вовсе.
     */
    bot: string
}

export interface ChallengeStatus {
    status: 'pending' | 'success' | 'expired'
    token?: string
    /**
     * OTP-R6c: у аккаунта с приложением-аутентификатором `token` — pending-ключ,
     * а не вход. Сначала код из приложения (`approval_type: 'totp'`).
     */
    requires_approval?: boolean
    approval_type?: string
}

type ErrorReason = 'expired' | 'timeout' | 'error'

// ── Config ──────────────────────────────────────

const DEFAULT_POLL_INTERVAL = 2_000   // 2 seconds
const MAX_POLL_ITERATIONS = 150     // 5 minutes @ 2s
const MAX_NETWORK_RETRIES = 3

// ── Service ─────────────────────────────────────

export class TelegramLoginPollingService {
    /**
     * Create a new login challenge on the backend.
     * Returns challenge_id (UUID) and TTL in seconds.
     */
    static async initChallenge(refCode?: string, resume?: string): Promise<ChallengeResponse> {
        const body: Record<string, string> = {}
        if (refCode) {
            body.ref_code = refCode
        }
        // FLOW-V5 Б4 — челлендж со страницы переноса: бот (`tgtradein_…`)
        // закрепит перенос этой сессии за аккаунтом и заговорит только о нём.
        if (resume) {
            body.resume = resume
        }

        const response = await httpClient.post<{
            data: {
                success: boolean
                challenge_id: string
                ttl: number
                bot?: string
            }
        }>('/auth/telegram-login-init', body)

        return {
            challenge_id: response.data.challenge_id,
            ttl: response.data.ttl,
            bot: (response.data.bot ?? '').replace(/^@/, ''),
        }
    }

    /**
     * Check a single challenge status.
     */
    static async checkStatus(challengeId: string, isCurrent?: () => boolean): Promise<ChallengeStatus> {
        const endpoint = `/auth/telegram-login-check?challenge_id=${encodeURIComponent(challengeId)}`
        // OTP-R3: с isCurrent ответ остановленного опроса ничего не применяет.
        const response = isCurrent
            ? await httpClient.guardedRequest<{ data: ChallengeStatus }>(endpoint, isCurrent, { method: 'GET' })
            : await httpClient.get<{ data: ChallengeStatus }>(endpoint)
        return response.data
    }

    /**
     * Start polling for challenge completion.
     *
     * @param challengeId  UUID from initChallenge
     * @param onSuccess    Called with auth token and full status on successful login
     * @param onError      Called with reason when polling fails
     * @param interval     Poll interval in ms (default 2000)
     * @returns A stop function to cancel polling manually
     */
    static startPolling(
        challengeId: string,
        onSuccess: (token: string, status: ChallengeStatus) => void,
        onError: (reason: ErrorReason) => void,
        interval: number = DEFAULT_POLL_INTERVAL,
    ): () => void {
        let iterations = 0
        let networkErrors = 0
        let stopped = false
        let timerId: ReturnType<typeof setTimeout> | null = null

        const poll = async () => {
            if (stopped) return

            iterations++

            // Timeout guard
            if (iterations > MAX_POLL_ITERATIONS) {
                stopped = true
                onError('timeout')
                return
            }

            try {
                const result = await TelegramLoginPollingService.checkStatus(challengeId, () => !stopped)
                // stop() во время запроса: поздний ответ уже никому не нужен.
                if (stopped) return
                networkErrors = 0 // reset on any successful request

                if (result.status === 'success' && result.token) {
                    stopped = true
                    onSuccess(result.token, result)
                    return
                }

                if (result.status === 'expired') {
                    stopped = true
                    onError('expired')
                    return
                }

                // Still pending → schedule next poll
                if (!stopped) {
                    timerId = setTimeout(poll, interval)
                }
            } catch {
                if (stopped) return
                networkErrors++
                if (networkErrors >= MAX_NETWORK_RETRIES) {
                    stopped = true
                    onError('error')
                    return
                }

                // Retry after interval
                if (!stopped) {
                    timerId = setTimeout(poll, interval)
                }
            }
        }

        // Start first poll after interval (challenge was just created)
        timerId = setTimeout(poll, interval)

        // Return stop function
        return () => {
            stopped = true
            if (timerId !== null) {
                clearTimeout(timerId)
                timerId = null
            }
        }
    }
}
